import { useState, useEffect, Component, ReactNode, useCallback } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { 
  Loader, 
  MessageSquare, 
  Target, 
  CheckCircle2, 
  RefreshCw,
  LightbulbIcon,
  Megaphone,
  Tag,
  BookText,
  MessageCircle,
  Search,
  Globe,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  AlertTriangle,
  Shield,
  Trophy,
  Zap,
  Heart,
  BarChart3,
  Star,
  PieChart,
  ChevronDown,
  AlignLeft,
  Share2,
  Image,
  Copy,
  Code,
  FileCode,
  GlobeIcon
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { motion } from "framer-motion";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";

// Define interfaces for the marketing vault content
interface USPsPerAudience {
  audience: string;
  usps: string[];
}

interface MarketingPositioning {
  title: string;
  description: string;
}

interface MarketingDescriptions {
  taglines: string[];
  shortDescriptions: string[];
  mediumDescriptions: string[];
  longDescription: string;
}

interface HormoziOffer {
  dreamOutcome: string;
  proof: string;
  speed: string;
  effortAndRisk: string;
  fullOffer: string;
}

interface PitchLibrary {
  oneSentence: string;
  elevatorPitch: string;
  coldEmailOpener: string;
  tweetHook: string;
}

interface WhatToSayWhen {
  question: string;
  answer: string;
}

interface SEOMetaKit {
  titleTag: string;
  metaDescription: string;
  ogTitle: string;
  ogDescription: string;
  suggestedAltText: string;
}

interface SocialMediaBios {
  twitter: string;
  instagram: string;
  facebook: string;
  linkedin: string;
}

interface MarketingVaultContent {
  uspsPerAudience: USPsPerAudience[];
  marketingPositioning: MarketingPositioning[];
  marketingDescriptions: MarketingDescriptions;
  hormozi: HormoziOffer;
  pitchLibrary: PitchLibrary;
  whatToSayWhen: WhatToSayWhen[];
  seoMetaKit: SEOMetaKit;
  socialMediaBios: SocialMediaBios;
}

// Component for section container
const SectionContainer = ({ 
  title, 
  description, 
  children,
  className
}: { 
  title: string; 
  description?: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={cn("mb-10 p-6 bg-white dark:bg-zinc-950 rounded-xl border border-zinc-100 dark:border-zinc-800 shadow-sm", className)}>
    <h2 className="text-2xl font-semibold gradient-text mb-2">
      {title}
    </h2>
    {description && <p className="text-zinc-500 mb-5">{description}</p>}
    {children}
  </div>
);

// Component for copyable content
const CopyableContent = ({ 
  content, 
  className 
}: { 
  content: string;
  className?: string;
}) => {
  const { toast } = useToast();
  const [copying, setCopying] = useState(false);
  
  const handleCopy = async () => {
    try {
      setCopying(true);
      await navigator.clipboard.writeText(content);
      toast({
        title: "Copied to clipboard",
        description: "Content has been copied to your clipboard.",
        duration: 2000,
      });
      // Add a small delay to show the copy animation
      setTimeout(() => setCopying(false), 1000);
    } catch (err) {
      setCopying(false);
      toast({
        title: "Copy failed",
        description: "Failed to copy content to clipboard.",
        variant: "destructive",
      });
    }
  };

  // Determine if this has a custom className that we want to preserve
  const hasCustomStyling = className && !className.includes("border-none") && !className.includes("shadow-none");

  return (
    <div className={cn(
      "relative group bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md",
      copying ? "border-blue-500 dark:border-blue-400 shadow-sm" : "",
      hasCustomStyling ? className : "p-4"
    )}>
      <div className={cn(
        "mb-2 overflow-auto whitespace-pre-wrap text-zinc-700 dark:text-zinc-300",
        !hasCustomStyling && "p-4"
      )}>
        {content}
      </div>
      <Button
        variant={copying ? "default" : "ghost"}
        size="sm"
        className={cn(
          "absolute top-2 right-2 transition-all",
          copying ? "bg-blue-600 text-white" : "opacity-0 group-hover:opacity-100"
        )}
        onClick={handleCopy}
        disabled={copying}
      >
        {copying ? (
          <span className="flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Copied
          </span>
        ) : "Copy"}
      </Button>
    </div>
  );
};

// Define a more detailed loading skeleton for different section types
const SectionSkeleton = ({ type = "default" }: { type?: "cards" | "tabs" | "accordion" | "default" }) => {
  switch (type) {
    case "cards":
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden">
              <div className="p-4 space-y-4">
                <Skeleton className="h-6 w-3/4" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                  <Skeleton className="h-4 w-4/5" />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    case "tabs":
      return (
        <div className="space-y-4">
          <div className="flex space-x-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-24 rounded-md" />
            ))}
          </div>
          <div className="space-y-3 mt-4">
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
          </div>
        </div>
      );
    case "accordion":
      return (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="border border-zinc-200 dark:border-zinc-800 rounded-md p-4">
              <Skeleton className="h-6 w-3/4 mb-1" />
            </div>
          ))}
        </div>
      );
    default:
      return (
        <div className="space-y-4">
          <Skeleton className="h-16 w-full rounded-md" />
          <Skeleton className="h-16 w-full rounded-md" />
        </div>
      );
  }
};

// Enhanced error state component with retry button and better styling
const ErrorState = ({ 
  error, 
  onRetry,
  className 
}: { 
  error: string; 
  onRetry: () => void;
  className?: string;
}) => (
  <div className={cn("rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6", className)}>
    <div className="flex flex-col items-center justify-center text-center">
      <div className="bg-red-100 dark:bg-red-900/30 p-3 rounded-full mb-4">
        <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
      </div>
      <h3 className="text-lg font-medium text-red-800 dark:text-red-300 mb-2">Unable to Load Content</h3>
      <p className="text-red-600 dark:text-red-400 mb-4 max-w-md">
        {error}
      </p>
      <Button 
        onClick={onRetry}
        variant="outline" 
        className="gap-2 border-red-300 hover:bg-red-100 dark:border-red-800 dark:hover:bg-red-900/30"
      >
        <RefreshCw className="h-4 w-4" />
        Try Again
      </Button>
    </div>
  </div>
);

// Empty state component
const EmptyState = ({ 
  onGenerate 
}: { 
  onGenerate: () => void;
}) => (
  <div className="flex flex-col items-center justify-center h-full py-16">
    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-full mb-4">
      <MessageSquare className="h-8 w-8 text-blue-600 dark:text-blue-400" />
    </div>
    <h3 className="text-xl font-medium text-zinc-900 dark:text-zinc-100 mb-2">No Marketing Content Yet</h3>
    <p className="text-zinc-500 dark:text-zinc-400 max-w-md text-center mb-6">
      Generate comprehensive marketing content based on your website analysis to help you communicate your value effectively.
    </p>
    <Button
      onClick={onGenerate}
      className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground"
    >
      <RefreshCw className="h-4 w-4" />
      Generate Marketing Content
    </Button>
  </div>
);

// Simple Error Boundary component
class ErrorBoundary extends Component<
  { children: ReactNode; fallback?: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; fallback?: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error("Error caught by boundary:", error);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="p-4 text-center text-zinc-500 border border-zinc-200 dark:border-zinc-800 rounded-md">
          Something went wrong rendering this section.
        </div>
      );
    }

    return this.props.children;
  }
}

const MarketingVaultPage = () => {
  const { id } = useParams<{ id: string }>();
  const [content, setContent] = useState<MarketingVaultContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasCrawlData, setHasCrawlData] = useState<boolean | null>(null);
  const { toast } = useToast();

  // Check if there's website analysis data available
  const checkAnalysisExists = async () => {
    if (!id) return false;
    
    try {
      const { data, error } = await supabase
        .from("website_crawls")
        .select("id")
        .eq("project_id", id)
        .eq("status", "completed")
        .limit(1)
        .maybeSingle();
        
      if (error) {
        console.error("Error checking for analysis data:", error);
        return false;
      }
      
      setHasCrawlData(!!data);
      return !!data;
    } catch (err) {
      console.error("Failed to check for analysis data:", err);
      setHasCrawlData(false);
      return false;
    }
  };

  // Define fetchContent outside useEffect so it can be reused
  const fetchContent = useCallback(async (showRefreshState = false) => {
    if (!id) {
      setLoading(false);
      setError("No project ID provided");
      return;
    }

    if (showRefreshState) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      // Define a custom interface to match the expected structure
      interface MarketingVaultRecord {
        id: string;
        project_id: string;
        content: any; // Will be cast to MarketingVaultContent later
        created_at: string;
        updated_at: string;
      }
      
      // Use a more aggressive type casting approach
      const response = await (supabase as any)
        .from('marketing_vault')
        .select('*')
        .eq('project_id', id)
        .maybeSingle();
        
      const { data, error } = response;

      if (error) {
        console.error("Supabase query error:", error.message, error.code, error.details);
        throw new Error(`Database error: ${error.message}`);
      }

      if (data && data.content) {
        setContent(data.content as MarketingVaultContent);
      } else {
        console.log("No content found yet, showing empty state");
        setContent(null);
      }
    } catch (e: any) {
      console.error("Failed to fetch marketing content:", e);
      
      // Provide more specific error messages based on error type
      let errorMessage = "Something went wrong while loading content.";
      
      if (e instanceof Error) {
        if (e.message.includes("network")) {
          errorMessage = "Network error. Please check your internet connection and try again.";
        } else if (e.message.includes("permission") || e.message.includes("authorization")) {
          errorMessage = "You don't have permission to access this content.";
        } else if (e.message.includes("Database")) {
          errorMessage = `Database error: ${e.message.replace("Database error: ", "")}`;
        } else {
          errorMessage = e.message;
        }
      }
      
      setError(errorMessage);
      
      if (!showRefreshState) {
        toast({
          variant: "destructive",
          title: "Error",
          description: errorMessage,
        });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id, toast]);

  useEffect(() => {
    fetchContent();
    checkAnalysisExists();
  }, [fetchContent, id]);

  const handleRefresh = () => {
    fetchContent(true);
  };

  const generateContent = async () => {
    if (!id) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No project ID provided",
      });
      return;
    }

    // Check if website analysis data exists
    if (!hasCrawlData) {
      const hasData = await checkAnalysisExists();
      if (!hasData) {
        toast({
          variant: "destructive",
          title: "No Analysis Data",
          description: "You need to analyze the website first before generating marketing content.",
        });
        return;
      }
    }

    setGenerating(true);
    setError(null);

    try {
      // Define API endpoint - Use hardcoded Supabase Functions URL
      const baseUrl = import.meta.env.VITE_SUPABASE_URL 
        ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`
        : "https://ngfwjlgmdcoxewihfmlt.supabase.co/functions/v1";
      const endpoint = `${baseUrl}/generate-marketing-vault`;

      // Get current user for auth
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error("User not authenticated");
      }

      // Make request to edge function
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({
          projectId: id,
          userId: user.id,
        }),
      });
      
      // Handle network errors
      if (!response.ok) {
        let errorMessage = "Failed to generate content";
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
          
          // Handle specific error cases
          if (response.status === 404) {
            errorMessage = "No website analysis data found. Please analyze your website first.";
          } else if (response.status === 429) {
            errorMessage = "Rate limit exceeded. Please try again later.";
          } else if (response.status >= 500) {
            errorMessage = "Server error. The system is currently experiencing issues.";
          }
        } catch (e) {
          // If we can't parse the error JSON, just use the status text
          errorMessage = response.statusText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      
      if (result.status === "success") {
        toast({
          title: "Success",
          description: "Marketing content generated successfully.",
        });
        
        // Refresh content
        await fetchContent();
      } else {
        throw new Error(result.error || "Failed to generate content");
      }
    } catch (error) {
      console.error("Error generating marketing content:", error);
      
      // Provide a user-friendly error message
      let errorMessage = "Failed to generate marketing content. Please try again.";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  // Loading skeleton UI - enhanced with more detail and structure
  if (loading) {
    return (
      <div className="container py-8 max-w-6xl animate-in fade-in duration-300">
        <div className="flex justify-between items-center mb-8">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
        
        {/* USPs Section Skeleton */}
        <div className="mb-8">
          <Skeleton className="h-7 w-48 mb-2" />
          <Skeleton className="h-4 w-96 mb-4" />
          <SectionSkeleton type="cards" />
        </div>
        
        {/* Marketing Positioning Skeleton */}
        <div className="mb-8">
          <Skeleton className="h-7 w-64 mb-2" />
          <Skeleton className="h-4 w-80 mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="border border-zinc-200 dark:border-zinc-800 rounded-lg p-4">
                <Skeleton className="h-8 w-8 mb-3" />
                <Skeleton className="h-6 w-3/4 mb-2" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Descriptions Section Skeleton */}
        <div className="mb-8">
          <Skeleton className="h-7 w-56 mb-2" />
          <Skeleton className="h-4 w-80 mb-4" />
          <SectionSkeleton type="tabs" />
        </div>
        
        {/* Additional Sections Skeletons */}
        <div className="mb-8">
          <Skeleton className="h-7 w-48 mb-2" />
          <Skeleton className="h-4 w-64 mb-4" />
          <SectionSkeleton />
        </div>
        
        <div className="mb-8">
          <Skeleton className="h-7 w-40 mb-2" />
          <Skeleton className="h-4 w-72 mb-4" />
          <SectionSkeleton type="accordion" />
        </div>
      </div>
    );
  }

  // Error state - now using enhanced error component
  if (error) {
    return (
      <div className="container py-8 max-w-6xl">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold gradient-text">
              Marketing Vault
            </h1>
            <p className="text-zinc-500 mt-2">
              High-quality, ready-to-use marketing content based on your website analysis.
            </p>
          </div>
        </div>
        
        <ErrorState 
          error={error} 
          onRetry={handleRefresh}
        />
      </div>
    );
  }

  // No content state
  if (!content) {
    return (
      <div className="container py-8 max-w-6xl">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold gradient-text">
              Marketing Vault
            </h1>
            <p className="text-zinc-500 mt-2">
              High-quality, ready-to-use marketing content based on your website analysis.
            </p>
          </div>
        </div>
        
        <div className="flex flex-col items-center justify-center p-8 rounded-lg border border-zinc-200 dark:border-zinc-800">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-full mb-4">
            <MessageSquare className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-xl font-semibold mb-2">No Marketing Content Available</h2>
          <p className="text-center text-zinc-600 dark:text-zinc-400 mb-4">
            Generate comprehensive marketing content based on your website analysis.
          </p>
          <Button
            onClick={generateContent}
            disabled={generating}
            className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {generating ? (
              <>
                <Loader className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Generate Marketing Content
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8 max-w-6xl">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold gradient-text">
            Marketing Vault
          </h1>
          <p className="text-zinc-500 mt-2">
            High-quality, ready-to-use marketing content based on your website analysis.
          </p>
        </div>
        
        <Button
          onClick={generating ? undefined : refreshing ? handleRefresh : generateContent}
          disabled={generating || refreshing}
          className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          {generating ? (
            <>
              <Loader className="h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : refreshing ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin" />
              Refreshing...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4" />
              Regenerate
            </>
          )}
        </Button>
      </div>

      {/* USPs Per Audience Section */}
      <ErrorBoundary>
        <SectionContainer title="USPs Per Audience" description="Key selling points tailored to each audience segment">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {content && content.uspsPerAudience && content.uspsPerAudience.length > 0 ? (
              content.uspsPerAudience.map((audienceUSP, index) => {
                // Create unique colors for each audience card
                const colors = [
                  { bg: "from-blue-50 to-blue-50 dark:from-blue-950/40 dark:to-blue-950/40", 
                    border: "border-blue-100 dark:border-blue-900/30", 
                    icon: "bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400",
                    check: "text-blue-600 dark:text-blue-400" },
                  { bg: "from-green-50 to-emerald-50 dark:from-green-950/40 dark:to-emerald-950/40", 
                    border: "border-green-100 dark:border-green-900/30", 
                    icon: "bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400",
                    check: "text-green-600 dark:text-green-400" },
                  { bg: "from-amber-50 to-yellow-50 dark:from-amber-950/40 dark:to-yellow-950/40", 
                    border: "border-amber-100 dark:border-amber-900/30", 
                    icon: "bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400",
                    check: "text-amber-600 dark:text-amber-400" },
                  { bg: "from-rose-50 to-pink-50 dark:from-rose-950/40 dark:to-pink-950/40", 
                    border: "border-rose-100 dark:border-rose-900/30", 
                    icon: "bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-400",
                    check: "text-rose-600 dark:text-rose-400" },
                ];
                
                const colorSet = colors[index % colors.length];
                
                return (
                  <div key={index} className={`p-5 bg-gradient-to-r ${colorSet.bg} rounded-lg border ${colorSet.border} overflow-hidden`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`p-2 rounded-lg ${colorSet.icon}`}>
                        <Target className="h-5 w-5" />
                      </div>
                      <h3 className="text-xl font-semibold">{audienceUSP.audience}</h3>
                    </div>
                    
                    <ul className="space-y-3 pl-2">
                      {audienceUSP.usps && audienceUSP.usps.map((usp, uspIndex) => (
                        <li key={uspIndex} className="flex items-start gap-3">
                          <div className="mt-1 flex-shrink-0">
                            <CheckCircle2 className={`h-4 w-4 ${colorSet.check}`} />
                          </div>
                          <span className="text-zinc-700 dark:text-zinc-300">{usp}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })
            ) : (
              <div className="col-span-2 p-4 text-center text-zinc-500">
                No audience USPs available
              </div>
            )}
          </div>
        </SectionContainer>
      </ErrorBoundary>

      {/* Marketing Positioning Section */}
      <ErrorBoundary>
        <SectionContainer title="Marketing Positioning Options" description="Distinct angles to highlight your competitive edge">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {content && content.marketingPositioning && content.marketingPositioning.length > 0 ? (
              content.marketingPositioning.map((position, index) => {
                // Alternate between different icons
                const icons = [
                  <Trophy className="h-6 w-6 text-blue-600 dark:text-blue-500" />,
                  <Zap className="h-6 w-6 text-amber-600 dark:text-amber-500" />,
                  <Heart className="h-6 w-6 text-rose-600 dark:text-rose-500" />,
                  <BarChart3 className="h-6 w-6 text-emerald-600 dark:text-emerald-500" />,
                  <Star className="h-6 w-6 text-blue-600 dark:text-blue-500" />,
                  <PieChart className="h-6 w-6 text-cyan-600 dark:text-cyan-500" />
                ];
                
                // Alternate between different style variants
                const variants = [
                  "bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-950/30 dark:to-blue-950/30 border-blue-100 dark:border-blue-900/30",
                  "bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-100 dark:border-amber-900/30",
                  "bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/30 dark:to-pink-950/30 border-rose-100 dark:border-rose-900/30",
                  "bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30 border-emerald-100 dark:border-emerald-900/30",
                  "bg-gradient-to-br from-blue-50 to-violet-50 dark:from-blue-950/30 dark:to-violet-950/30 border-blue-100 dark:border-blue-900/30",
                  "bg-gradient-to-br from-cyan-50 to-sky-50 dark:from-cyan-950/30 dark:to-sky-950/30 border-cyan-100 dark:border-cyan-900/30"
                ];
                
                return (
                  <div 
                    key={index} 
                    className={`p-6 rounded-xl border overflow-hidden shadow-sm ${variants[index % variants.length]}`}
                  >
                    <div className="mb-4">
                      {icons[index % icons.length]}
                    </div>
                      <h3 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
                        {position.title}
                      </h3>
                      <p className="text-zinc-700 dark:text-zinc-300">
                        {position.description}
                      </p>
                    </div>
                );
              })
            ) : (
              <div className="col-span-3 p-4 text-center text-zinc-500">
                No positioning options available
              </div>
            )}
          </div>
        </SectionContainer>
      </ErrorBoundary>

      {/* Pitch Library Section */}
      <ErrorBoundary>
        <SectionContainer title="Pitch Library" description="Ready-to-use pitches for different contexts">
          {content && content.pitchLibrary ? (
            <div className="space-y-8">
              {/* 1-Sentence Pitch */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-xl flex flex-col">
                  <div className="flex items-center mb-3">
                    <div className="bg-blue-500 p-2 rounded-md mr-3">
                      <BookText className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-medium text-blue-900 dark:text-blue-300">1-Sentence Pitch</h3>
                      <p className="text-sm text-blue-700 dark:text-blue-400">Perfect for quick intros</p>
                    </div>
                  </div>
                </div>
                
                <div className="md:col-span-3 bg-gradient-to-r from-white to-blue-50 dark:from-zinc-900 dark:to-blue-900/10 p-5 rounded-xl border border-blue-100 dark:border-blue-900/20 relative group">
                  <div className="text-zinc-700 dark:text-zinc-300">
                    {content.pitchLibrary.oneSentence || 'Not available'}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(content.pitchLibrary.oneSentence);
                        toast({
                          title: "Copied to clipboard",
                          description: "1-Sentence Pitch copied to your clipboard.",
                          duration: 2000,
                        });
                      } catch (err) {
                        toast({
                          title: "Copy failed",
                          description: "Failed to copy content to clipboard.",
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Cold Email Opener */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-xl flex flex-col">
                  <div className="flex items-center mb-3">
                    <div className="bg-blue-500 p-2 rounded-md mr-3">
                      <MessageCircle className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-medium text-blue-900 dark:text-blue-300">Cold Email Opener</h3>
                      <p className="text-sm text-blue-700 dark:text-blue-400">For outreach campaigns</p>
                    </div>
                  </div>
                </div>
                
                <div className="md:col-span-3 bg-gradient-to-r from-white to-blue-50 dark:from-zinc-900 dark:to-blue-900/10 p-5 rounded-xl border border-blue-100 dark:border-blue-900/20 relative group">
                  <div className="text-zinc-700 dark:text-zinc-300">
                    {content.pitchLibrary.coldEmailOpener || 'Not available'}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(content.pitchLibrary.coldEmailOpener);
                        toast({
                          title: "Copied to clipboard",
                          description: "Cold Email Opener copied to your clipboard.",
                          duration: 2000,
                        });
                      } catch (err) {
                        toast({
                          title: "Copy failed",
                          description: "Failed to copy content to clipboard.",
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* 30-Second Elevator Pitch */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-emerald-50 dark:bg-emerald-900/20 p-5 rounded-xl flex flex-col">
                  <div className="flex items-center mb-3">
                    <div className="bg-emerald-500 p-2 rounded-md mr-3">
                      <MessageSquare className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-medium text-emerald-900 dark:text-emerald-300">30-Second Elevator Pitch</h3>
                      <p className="text-sm text-emerald-700 dark:text-emerald-400">For in-person networking</p>
                    </div>
                  </div>
                </div>
                
                <div className="md:col-span-3 bg-gradient-to-r from-white to-emerald-50 dark:from-zinc-900 dark:to-emerald-900/10 p-5 rounded-xl border border-emerald-100 dark:border-emerald-900/20 relative group">
                  <div className="text-zinc-700 dark:text-zinc-300">
                    {content.pitchLibrary.elevatorPitch || 'Not available'}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(content.pitchLibrary.elevatorPitch);
                        toast({
                          title: "Copied to clipboard",
                          description: "30-Second Elevator Pitch copied to your clipboard.",
                          duration: 2000,
                        });
                      } catch (err) {
                        toast({
                          title: "Copy failed",
                          description: "Failed to copy content to clipboard.",
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Tweet Hook / Scroll Stopper */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-sky-50 dark:bg-sky-900/20 p-5 rounded-xl flex flex-col">
                  <div className="flex items-center mb-3">
                    <div className="bg-sky-500 p-2 rounded-md mr-3">
                      <Twitter className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-medium text-sky-900 dark:text-sky-300">Tweet Hook / Scroll Stopper</h3>
                      <p className="text-sm text-sky-700 dark:text-sky-400">For social media posts</p>
                    </div>
                  </div>
                </div>
                
                <div className="md:col-span-3 bg-gradient-to-r from-white to-sky-50 dark:from-zinc-900 dark:to-sky-900/10 p-5 rounded-xl border border-sky-100 dark:border-sky-900/20 relative group">
                  <div className="text-zinc-700 dark:text-zinc-300">
                    {content.pitchLibrary.tweetHook || 'Not available'}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(content.pitchLibrary.tweetHook);
                        toast({
                          title: "Copied to clipboard",
                          description: "Tweet Hook copied to your clipboard.",
                          duration: 2000,
                        });
                      } catch (err) {
                        toast({
                          title: "Copy failed",
                          description: "Failed to copy content to clipboard.",
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 text-center text-zinc-500">
              No pitch library available
            </div>
          )}
        </SectionContainer>
      </ErrorBoundary>

      {/* Marketing Descriptions Section */}
      <ErrorBoundary>
        <SectionContainer title="Product Descriptions" description="Ready-to-use copy for various marketing channels">
          {content && content.marketingDescriptions ? (
            <Tabs defaultValue="taglines" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="taglines">Taglines</TabsTrigger>
                <TabsTrigger value="short">Short Descriptions</TabsTrigger>
                <TabsTrigger value="medium">Medium Descriptions</TabsTrigger>
                <TabsTrigger value="long">Long Description</TabsTrigger>
              </TabsList>
              
              <TabsContent value="taglines" className="mt-0">
                <div className="grid grid-cols-1 gap-4">
                  {content.marketingDescriptions.taglines && content.marketingDescriptions.taglines.length > 0 ? (
                    content.marketingDescriptions.taglines.map((tagline, index) => (
                      <div 
                        key={index} 
                        className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-5 relative group"
                      >
                        <p className="text-lg text-zinc-800 dark:text-zinc-200 mb-2 leading-relaxed">
                          {tagline}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(tagline);
                              toast({
                                title: "Copied to clipboard",
                                description: "Tagline copied to your clipboard.",
                                duration: 2000,
                              });
                            } catch (err) {
                              toast({
                                title: "Copy failed",
                                description: "Failed to copy content to clipboard.",
                                variant: "destructive",
                              });
                            }
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 text-center text-zinc-500">
                      No taglines available
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="short" className="mt-0">
                <div className="grid grid-cols-1 gap-4">
                  {content.marketingDescriptions.shortDescriptions && content.marketingDescriptions.shortDescriptions.length > 0 ? (
                    content.marketingDescriptions.shortDescriptions.map((desc, index) => (
                      <div 
                        key={index} 
                        className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-5 relative group"
                      >
                        <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
                          {desc}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(desc);
                              toast({
                                title: "Copied to clipboard",
                                description: "Short description copied to your clipboard.",
                                duration: 2000,
                              });
                            } catch (err) {
                              toast({
                                title: "Copy failed",
                                description: "Failed to copy content to clipboard.",
                                variant: "destructive",
                              });
                            }
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 text-center text-zinc-500">
                      No short descriptions available
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="medium" className="mt-0">
                <div className="grid grid-cols-1 gap-4">
                  {content.marketingDescriptions.mediumDescriptions && content.marketingDescriptions.mediumDescriptions.length > 0 ? (
                    content.marketingDescriptions.mediumDescriptions.map((desc, index) => (
                      <div 
                        key={index} 
                        className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-5 relative group"
                      >
                        <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
                          {desc}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(desc);
                              toast({
                                title: "Copied to clipboard",
                                description: "Medium description copied to your clipboard.",
                                duration: 2000,
                              });
                            } catch (err) {
                              toast({
                                title: "Copy failed",
                                description: "Failed to copy content to clipboard.",
                                variant: "destructive",
                              });
                            }
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 text-center text-zinc-500">
                      No medium descriptions available
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="long" className="mt-0">
                {content.marketingDescriptions.longDescription ? (
                  <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-5 relative group">
                    <div className="text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap leading-relaxed">
                      {content.marketingDescriptions.longDescription}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(content.marketingDescriptions.longDescription);
                          toast({
                            title: "Copied to clipboard",
                            description: "Long description copied to your clipboard.",
                            duration: 2000,
                          });
                        } catch (err) {
                          toast({
                            title: "Copy failed",
                            description: "Failed to copy content to clipboard.",
                            variant: "destructive",
                          });
                        }
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="p-4 text-center text-zinc-500">
                    No long description available
                  </div>
                )}
              </TabsContent>
            </Tabs>
          ) : (
            <div className="p-4 text-center text-zinc-500">
              No product descriptions available
            </div>
          )}
        </SectionContainer>
      </ErrorBoundary>

      {/* Hormozi-Style Offer Section */}
      <ErrorBoundary>
        <SectionContainer title="Irresistible Offer Script" description="Based on Alex Hormozi's principles">
          {content && content.hormozi ? (
            <div className="space-y-5">
              {/* Full Offer - Main Section */}
              {content.hormozi.fullOffer && (
                <div className="p-6 bg-gradient-to-r from-blue-600 to-sky-500 dark:from-blue-900 dark:to-blue-900 rounded-lg border border-blue-500 dark:border-blue-800 shadow-md mb-6">
                  <h3 className="text-xl font-semibold text-white mb-3 flex items-center">
                    <Megaphone className="h-5 w-5 mr-2" />
                    Complete Offer Statement
                  </h3>
                  <div className="bg-white/10 dark:bg-black/20 p-5 rounded-lg backdrop-blur-sm">
                    <p className="text-lg text-white leading-relaxed">
                      {content.hormozi.fullOffer}
                    </p>
                </div>
                  <div className="mt-4 flex justify-end">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="bg-white/20 hover:bg-white/30 text-white border-none"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(content.hormozi.fullOffer);
                          toast({
                            title: "Copied to clipboard",
                            description: "Offer has been copied to your clipboard.",
                            duration: 2000,
                          });
                        } catch (err) {
                          toast({
                            title: "Copy failed",
                            description: "Failed to copy content to clipboard.",
                            variant: "destructive",
                          });
                        }
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" /> Copy Full Offer
                    </Button>
                </div>
            </div>
          )}
              
              {/* Individual Components */}
              <div className="pt-2">
                <h3 className="text-lg font-medium text-zinc-800 dark:text-zinc-200 mb-4">Offer Components</h3>
                
                <div className="space-y-4">
                  <div className="p-5 bg-gradient-to-r from-blue-50 to-blue-50 dark:from-blue-950/40 dark:to-blue-950/40 rounded-lg border border-blue-100 dark:border-blue-900/30">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="bg-blue-100 dark:bg-blue-900/50 p-2 rounded-lg">
                        <LightbulbIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
                      <h4 className="text-lg font-medium text-blue-700 dark:text-blue-400">Dream Outcome</h4>
            </div>
                    <div className="relative group text-zinc-700 dark:text-zinc-300 pl-10">
                      {content.hormozi.dreamOutcome || 'Not available'}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(content.hormozi.dreamOutcome);
                            toast({
                              title: "Copied to clipboard",
                              description: "Dream Outcome copied to your clipboard.",
                              duration: 2000,
                            });
                          } catch (err) {
                            toast({
                              title: "Copy failed",
                              description: "Failed to copy content to clipboard.",
                              variant: "destructive",
                            });
                          }
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
            </div>
                  
                  <div className="p-5 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/40 dark:to-emerald-950/40 rounded-lg border border-green-100 dark:border-green-900/30">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="bg-green-100 dark:bg-green-900/50 p-2 rounded-lg">
                        <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                      <h4 className="text-lg font-medium text-green-700 dark:text-green-400">Proof & Confidence</h4>
                  </div>
                    <div className="relative group text-zinc-700 dark:text-zinc-300 pl-10">
                      {content.hormozi.proof || 'Not available'}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(content.hormozi.proof);
                            toast({
                              title: "Copied to clipboard",
                              description: "Proof & Confidence copied to your clipboard.",
                              duration: 2000,
                            });
                          } catch (err) {
                            toast({
                              title: "Copy failed",
                              description: "Failed to copy content to clipboard.",
                              variant: "destructive",
                            });
                          }
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-5 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/40 dark:to-yellow-950/40 rounded-lg border border-amber-100 dark:border-amber-900/30">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="bg-amber-100 dark:bg-amber-900/50 p-2 rounded-lg">
                        <RefreshCw className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <h4 className="text-lg font-medium text-amber-700 dark:text-amber-400">Speed</h4>
                    </div>
                    <div className="relative group text-zinc-700 dark:text-zinc-300 pl-10">
                      {content.hormozi.speed || 'Not available'}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(content.hormozi.speed);
                            toast({
                              title: "Copied to clipboard",
                              description: "Speed copied to your clipboard.",
                              duration: 2000,
                            });
                          } catch (err) {
                            toast({
                              title: "Copy failed",
                              description: "Failed to copy content to clipboard.",
                              variant: "destructive",
                            });
                          }
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-5 bg-gradient-to-r from-blue-50 to-violet-50 dark:from-blue-950/40 dark:to-violet-950/40 rounded-lg border border-blue-100 dark:border-blue-900/30">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="bg-blue-100 dark:bg-blue-900/50 p-2 rounded-lg">
                        <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                      <h4 className="text-lg font-medium text-blue-700 dark:text-blue-400">Minimized Effort & Risk</h4>
                </div>
                    <div className="relative group text-zinc-700 dark:text-zinc-300 pl-10">
                      {content.hormozi.effortAndRisk || 'Not available'}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(content.hormozi.effortAndRisk);
                            toast({
                              title: "Copied to clipboard",
                              description: "Minimized Effort & Risk copied to your clipboard.",
                              duration: 2000,
                            });
                          } catch (err) {
                            toast({
                              title: "Copy failed",
                              description: "Failed to copy content to clipboard.",
                              variant: "destructive",
                            });
                          }
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 text-center text-zinc-500">
              No offer script available
            </div>
          )}
        </SectionContainer>
      </ErrorBoundary>

      {/* Social Media Bio Library Section */}
      <ErrorBoundary>
        <SectionContainer title="Social Media Bio Library" description="Platform-specific bios optimized for each network">
          {content && content.socialMediaBios ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-r from-blue-50 to-sky-50 dark:from-blue-950/30 dark:to-sky-950/30 rounded-xl border border-blue-100 dark:border-blue-900/30 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-100/80 to-sky-100/80 dark:from-blue-900/40 dark:to-sky-900/40 p-4 flex items-center gap-3">
                  <div className="bg-[#1DA1F2] p-2 rounded-lg">
                    <Twitter className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-blue-800 dark:text-blue-300">Twitter/X</h3>
                    <p className="text-sm text-blue-600 dark:text-blue-400">160 characters max</p>
                  </div>
                </div>
                <div className="p-5 text-zinc-700 dark:text-zinc-300 relative group">
                  {content.socialMediaBios.twitter || 'Not available'}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(content.socialMediaBios.twitter);
                        toast({
                          title: "Copied to clipboard",
                          description: "Twitter bio copied to your clipboard.",
                          duration: 2000,
                        });
                      } catch (err) {
                        toast({
                          title: "Copy failed",
                          description: "Failed to copy content to clipboard.",
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-fuchsia-50 to-blue-50 dark:from-fuchsia-950/30 dark:to-blue-950/30 rounded-xl border border-fuchsia-100 dark:border-fuchsia-900/30 overflow-hidden">
                <div className="bg-gradient-to-r from-fuchsia-100/80 to-blue-100/80 dark:from-fuchsia-900/40 dark:to-blue-900/40 p-4 flex items-center gap-3">
                  <div className="bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] p-2 rounded-lg">
                    <Instagram className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-fuchsia-800 dark:text-fuchsia-300">Instagram</h3>
                    <p className="text-sm text-fuchsia-600 dark:text-fuchsia-400">150 characters max</p>
                  </div>
                </div>
                <div className="p-5 text-zinc-700 dark:text-zinc-300 relative group">
                  {content.socialMediaBios.instagram || 'Not available'}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(content.socialMediaBios.instagram);
                        toast({
                          title: "Copied to clipboard",
                          description: "Instagram bio copied to your clipboard.",
                          duration: 2000,
                        });
                      } catch (err) {
                        toast({
                          title: "Copy failed",
                          description: "Failed to copy content to clipboard.",
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-blue-50 to-blue-50 dark:from-blue-950/30 dark:to-blue-950/30 rounded-xl border border-blue-100 dark:border-blue-900/30 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-100/80 to-blue-100/80 dark:from-blue-900/40 dark:to-blue-900/40 p-4 flex items-center gap-3">
                  <div className="bg-[#1877F2] p-2 rounded-lg">
                    <Facebook className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-blue-800 dark:text-blue-300">Facebook</h3>
                    <p className="text-sm text-blue-600 dark:text-blue-400">101 characters max</p>
                  </div>
                </div>
                <div className="p-5 text-zinc-700 dark:text-zinc-300 relative group">
                  {content.socialMediaBios.facebook || 'Not available'}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(content.socialMediaBios.facebook);
                        toast({
                          title: "Copied to clipboard",
                          description: "Facebook bio copied to your clipboard.",
                          duration: 2000,
                        });
                      } catch (err) {
                        toast({
                          title: "Copy failed",
                          description: "Failed to copy content to clipboard.",
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-sky-50 to-blue-50 dark:from-sky-950/30 dark:to-blue-950/30 rounded-xl border border-sky-100 dark:border-sky-900/30 overflow-hidden">
                <div className="bg-gradient-to-r from-sky-100/80 to-blue-100/80 dark:from-sky-900/40 dark:to-blue-900/40 p-4 flex items-center gap-3">
                  <div className="bg-[#0A66C2] p-2 rounded-lg">
                    <Linkedin className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-sky-800 dark:text-sky-300">LinkedIn</h3>
                    <p className="text-sm text-sky-600 dark:text-sky-400">Profile summary (max 300 words)</p>
                  </div>
                </div>
                <div className="p-5 text-zinc-700 dark:text-zinc-300 relative group">
                  {content.socialMediaBios.linkedin || 'Not available'}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(content.socialMediaBios.linkedin);
                        toast({
                          title: "Copied to clipboard",
                          description: "LinkedIn bio copied to your clipboard.",
                          duration: 2000,
                        });
                      } catch (err) {
                        toast({
                          title: "Copy failed",
                          description: "Failed to copy content to clipboard.",
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 text-center text-zinc-500">
              No social media bios available
            </div>
          )}
        </SectionContainer>
      </ErrorBoundary>
    </div>
  );
};

export default MarketingVaultPage; 